import { useEffect, useState } from "react";
import { OnboardingAPI } from "../services/onboarding.service";
import { logger } from "../utils/logger";

// ── Types ────────────────────────────────────────────────────────────────────

export type UserRole = "Admin" | "Doctor" | "Receptionist" | "PlatformAdmin" | "";

/** How much of a Settings page the current user gets. */
export type PageAccess = "none" | "view" | "edit";

export interface RoleState {
  role: UserRole;
  can_prescribe: boolean;
  isAdmin: boolean;
  isDoctor: boolean;
  isReception: boolean;
  isPlatformAdmin: boolean;
  isLoaded: boolean;
  /** Effective RBAC grants. Empty when `permissionsKnown` is false. */
  permissions: Set<string>;
  /**
   * Whether the server actually told us the grants. False against a backend
   * that predates /auth/me returning permission_codes — callers must then fall
   * back to role-based gating rather than treating "no codes" as "no access",
   * which would blank the whole Settings nav.
   */
  permissionsKnown: boolean;
  /** Holds this permission code. False when the grants aren't known. */
  can: (code: string) => boolean;
  /**
   * Permission check that falls back to a legacy role decision when the server
   * never sent the grants (`permissionsKnown === false`).
   *
   * Use this — not `can` — for gating UI. `can` returns false against a backend
   * that predates permission_codes on /auth/me, which would hide every gated
   * button in the app rather than leaving it as it was. Mirrors the server's
   * LEGACY_ROLE_GRANTS: "unknown" means "behave exactly as before", never "deny".
   *
   *   canOr("records.delete", isAdmin)   // was Admin-only before RBAC
   *   canOr("patients.create", true)     // was shown to every clinic role
   */
  canOr: (code: string, legacyFallback: boolean) => boolean;
  /** Holds at least one of these codes. Same fallback rule as `canOr`. */
  canAnyOr: (codes: string[], legacyFallback: boolean) => boolean;
  /** Access level for a Settings left-nav section id ("clinic", "finance", …). */
  pageAccess: (section: string) => PageAccess;
}

// Self-scoped Settings pages: your own profile, your own trusted devices, and
// raising a support ticket. They carry no settings_<id>.* pair in the backend
// catalog on purpose, so every authenticated user keeps them.
const ALWAYS_VISIBLE_SECTIONS = new Set(["profile", "devices", "support"]);

// ── Cache ────────────────────────────────────────────────────────────────────
// Piggy-backs on the same localStorage key that RequireAuth maintains so we
// never issue a second /auth/me when the auth cache is already warm.

const AUTH_CACHE_KEY    = "kaagazy_auth_cache";
const AUTH_CACHE_TTL_MS = 72 * 60 * 60 * 1000;

// ── Dev role override ─────────────────────────────────────────────────────────
// Separate key that RequireAuth never touches, so spoofed roles survive the
// getMe() call on reload (which would otherwise overwrite the auth cache).
const DEV_OVERRIDE_KEY = "kaagazy_dev_role_override";

export function setDevRoleOverride(role: string, can_prescribe: boolean): void {
  if (process.env.NODE_ENV !== "development") return;
  localStorage.setItem(DEV_OVERRIDE_KEY, JSON.stringify({ role, can_prescribe }));
}

export function clearDevRoleOverride(): void {
  localStorage.removeItem(DEV_OVERRIDE_KEY);
}

function readDevOverride(): { role: string; can_prescribe: boolean } | null {
  if (process.env.NODE_ENV !== "development") return null;
  try {
    const raw = localStorage.getItem(DEV_OVERRIDE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

interface AuthCache {
  userId:            string;
  role:              string;
  can_prescribe?:    boolean;
  permission_codes?: string[];
  onboarding_status: string;
  cachedAt:          number;
}

function readRoleCache(): Pick<AuthCache, "role" | "can_prescribe" | "permission_codes"> | null {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const parsed: AuthCache = JSON.parse(raw);
    if (Date.now() - parsed.cachedAt > AUTH_CACHE_TTL_MS) return null;
    return {
      role: parsed.role,
      can_prescribe: parsed.can_prescribe,
      permission_codes: parsed.permission_codes,
    };
  } catch {
    return null;
  }
}

export function patchRoleCache(
  role: string,
  can_prescribe: boolean,
  permission_codes?: string[],
): void {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    const parsed: Partial<AuthCache> = raw ? JSON.parse(raw) : { userId: "", onboarding_status: "" };
    parsed.can_prescribe = can_prescribe;
    parsed.role = role;
    // Only overwrite when the server actually sent codes — an older backend
    // must not blank a cache a newer one populated.
    if (permission_codes !== undefined) parsed.permission_codes = permission_codes;
    parsed.cachedAt = Date.now();
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(parsed));
  } catch { /* storage quota exceeded — non-fatal */ }
}

// ── Derived helpers ──────────────────────────────────────────────────────────

const LOADING_STATE: RoleState = {
  role: "",
  can_prescribe: false,
  isAdmin: false,
  isDoctor: false,
  isReception: false,
  isPlatformAdmin: false,
  isLoaded: false,
  permissions: new Set<string>(),
  permissionsKnown: false,
  can: () => false,
  // role is "" while loading, so every legacy fallback a caller passes evaluates
  // false here anyway — gates stay closed until isLoaded flips.
  canOr: (_code, legacyFallback) => legacyFallback,
  canAnyOr: (_codes, legacyFallback) => legacyFallback,
  pageAccess: (section: string) => (ALWAYS_VISIBLE_SECTIONS.has(section) ? "edit" : "none"),
};

function deriveState(
  role: string,
  can_prescribe: boolean,
  permission_codes?: string[],
): RoleState {
  const permissionsKnown = Array.isArray(permission_codes);
  const permissions = new Set(permission_codes ?? []);
  const can = (code: string) => permissions.has(code);
  const canOr = (code: string, legacyFallback: boolean) =>
    permissionsKnown ? permissions.has(code) : legacyFallback;
  const canAnyOr = (codes: string[], legacyFallback: boolean) =>
    permissionsKnown ? codes.some(c => permissions.has(c)) : legacyFallback;

  // Mirrors require_settings_page on the backend: edit implies view, and the
  // codes are derived from the nav id so there is no second mapping to drift.
  const pageAccess = (section: string): PageAccess => {
    if (ALWAYS_VISIBLE_SECTIONS.has(section)) return "edit";
    // Unknown grants (older backend) → defer to the legacy role gate rather
    // than hiding everything. Admin keeps full Settings; the other roles are
    // narrowed by SECTIONS_BY_ROLE in SettingsPage, exactly as before.
    if (!permissionsKnown) return role === "Admin" ? "edit" : "none";
    if (permissions.has(`settings_${section}.edit`)) return "edit";
    if (permissions.has(`settings_${section}.view`)) return "view";
    return "none";
  };

  return {
    role: role as UserRole,
    can_prescribe,
    isAdmin: role === "Admin",
    isDoctor: role === "Doctor",
    isReception: role === "Receptionist",
    isPlatformAdmin: role === "PlatformAdmin",
    isLoaded: true,
    permissions,
    permissionsKnown,
    can,
    canOr,
    canAnyOr,
    pageAccess,
  };
}

// ── Revalidation ──────────────────────────────────────────────────────────────
// Dedupe concurrent /auth/me calls when several components mount useRole in the
// same tick. A fresh call is issued on the next mount (after this one settles).
let _inFlightMe: ReturnType<typeof OnboardingAPI.getMe> | null = null;
function revalidateMe(): ReturnType<typeof OnboardingAPI.getMe> {
  if (!_inFlightMe) {
    const p = OnboardingAPI.getMe();
    _inFlightMe = p;
    // Clear the slot when it settles, handling BOTH outcomes here so this cleanup
    // chain can never become an unhandled rejection. A bare `.finally()` would
    // re-throw the failure (e.g. "Missing authorization header" when the session
    // token isn't ready), surfacing as an uncaught ApiError. Real callers still
    // receive the rejection via their own `.catch` on the returned promise.
    const clear = () => { if (_inFlightMe === p) _inFlightMe = null; };
    p.then(clear, clear);
  }
  return _inFlightMe;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useRole — returns the current user's role and prescribe flag.
 *
 * Resolution order:
 * 1. localStorage auth cache (warm path — instant first paint, may be stale)
 * 2. OnboardingAPI.getMe() — ALWAYS revalidated on mount (stale-while-revalidate)
 *
 * role is stable for a session, but `can_prescribe` flips the moment a
 * PlatformAdmin verifies the user's medical registration. Trusting the long-lived
 * cache pinned it to its old value until re-login, so credential gates (the Start
 * Visit modal, the dashboard KYC banner) never cleared. We now refresh it every
 * mount so a reload/navigation reflects verification without re-login.
 *
 * Returns `isLoaded: false` until the role is resolved so callers can defer
 * rendering role-sensitive UI instead of flashing incorrect content.
 */
export function useRole(): RoleState {
  const [state, setState] = useState<RoleState>(() => {
    // Dev override takes priority — survives RequireAuth overwriting the auth cache.
    const dev = readDevOverride();
    if (dev?.role) return deriveState(dev.role, dev.can_prescribe ?? false);
    // Synchronous fast path — read from cache before first render to avoid flash.
    // Only trust the cache when it actually carries can_prescribe: RequireAuth
    // writes this same cache key with role but WITHOUT can_prescribe, so trusting
    // `?? false` here would pin a credentialed clinician to can_prescribe=false and
    // never refetch. When it's absent, fall through to getMe() below to resolve it.
    const cached = readRoleCache();
    if (cached?.role && cached.can_prescribe !== undefined) {
      return deriveState(cached.role, cached.can_prescribe, cached.permission_codes);
    }
    return LOADING_STATE;
  });

  useEffect(() => {
    // Dev override wins and must not be revalidated against the server.
    if (readDevOverride()?.role) return;

    let cancelled = false;

    // Always revalidate — the cached value (if any) already painted; this refreshes
    // can_prescribe so verification takes effect without a re-login.
    revalidateMe()
      .then((me) => {
        if (cancelled) return;
        const role = me.role ?? "";
        const can_prescribe = me.can_prescribe ?? false;
        // Left undefined (not []) on an older backend, so permissionsKnown
        // stays false and gating falls back to the role.
        const permission_codes = me.permission_codes;
        patchRoleCache(role, can_prescribe, permission_codes);
        setState(deriveState(role, can_prescribe, permission_codes));
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error("useRole: getMe() failed", err instanceof Error ? err : undefined);
        // Keep whatever we already resolved from cache; only fall back to a fresh
        // read when nothing was loaded yet (don't blank a warm session when offline).
        setState((prev) => {
          if (prev.isLoaded) return prev;
          const fallback = readRoleCache();
          return deriveState(
            fallback?.role ?? "",
            fallback?.can_prescribe ?? false,
            fallback?.permission_codes,
          );
        });
      });

    return () => { cancelled = true; };
  // Revalidate once per mount — role is stable but can_prescribe flips on verify.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
